import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '../../lib/utils';
import {
  dismissIcon,
  errorCircleIcon,
  infoCircleIcon,
  successCircleIcon,
  warningTriangleIcon,
} from '../../icons';
import { Button } from './button';
import { Icon } from './icon';

export type ToastVariant = 'default' | 'success' | 'warning' | 'destructive';

export interface ToastOptions {
  title?: string;
  description: ReactNode;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastRecord {
  id: string;
  title?: string;
  description: ReactNode;
  variant: ToastVariant;
  duration: number;
}

type ToastShortcutOptions = Omit<ToastOptions, 'description' | 'variant'>;

interface ToastContextValue {
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  success: (description: ReactNode, options?: ToastShortcutOptions) => string;
  warning: (description: ReactNode, options?: ToastShortcutOptions) => string;
  error: (description: ReactNode, options?: ToastShortcutOptions) => string;
  info: (description: ReactNode, options?: ToastShortcutOptions) => string;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const toastVariants = cva(
  'pointer-events-auto flex w-full items-start gap-3 rounded-xl border p-4 shadow-lg ring-1 ring-black/5 backdrop-blur-sm',
  {
    variants: {
      variant: {
        default: 'border-slate-200 bg-white text-slate-900',
        success: 'border-green-200 bg-green-50/95 text-green-900',
        warning: 'border-yellow-200 bg-yellow-50/95 text-yellow-900',
        destructive: 'border-red-200 bg-red-50/95 text-red-900',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const iconMap: Record<ToastVariant, string> = {
  default: infoCircleIcon,
  success: successCircleIcon,
  warning: warningTriangleIcon,
  destructive: errorCircleIcon,
};

const iconColorMap: Record<ToastVariant, string> = {
  default: 'text-slate-500',
  success: 'text-green-500',
  warning: 'text-yellow-500',
  destructive: 'text-red-500',
};

const dismissColorMap: Record<ToastVariant, string> = {
  default: 'text-slate-400 hover:text-slate-600',
  success: 'text-green-500 hover:text-green-700',
  warning: 'text-yellow-500 hover:text-yellow-700',
  destructive: 'text-red-500 hover:text-red-700',
};

function ToastCard({ toast, onDismiss }: { toast: ToastRecord; onDismiss: (id: string) => void }) {
  useEffect(() => {
    if (!Number.isFinite(toast.duration) || toast.duration <= 0) return;

    const timeout = window.setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.duration, toast.id]);

  return (
    <div
      role={toast.variant === 'destructive' ? 'alert' : 'status'}
      aria-live={toast.variant === 'destructive' ? 'assertive' : 'polite'}
      aria-atomic="true"
      className={cn(toastVariants({ variant: toast.variant }))}
    >
      <Icon
        src={iconMap[toast.variant]}
        className={cn('mt-0.5 h-5 w-5', iconColorMap[toast.variant])}
      />
      <div className="min-w-0 flex-1">
        {toast.title && <p className="font-semibold leading-5">{toast.title}</p>}
        <div className={cn('text-sm leading-5', toast.title && 'mt-1')}>{toast.description}</div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onDismiss(toast.id)}
        className={cn(
          'h-6 w-6 shrink-0 rounded-full p-0 hover:bg-transparent',
          dismissColorMap[toast.variant]
        )}
        aria-label="Dismiss toast"
      >
        <Icon src={dismissIcon} className="h-4 w-4" />
      </Button>
    </div>
  );
}

export interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
  defaultDuration?: number;
}

export function ToastProvider({
  children,
  maxToasts = 5,
  defaultDuration = 4000,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback(
    ({ variant = 'default', duration = defaultDuration, ...options }: ToastOptions) => {
      nextId.current += 1;
      const id = `toast-${nextId.current}`;

      setToasts((current) =>
        [{ id, variant, duration, ...options }, ...current].slice(0, maxToasts)
      );

      return id;
    },
    [defaultDuration, maxToasts]
  );

  const info = useCallback(
    (description: ReactNode, options?: ToastShortcutOptions) =>
      toast({ ...options, description, variant: 'default' }),
    [toast]
  );

  const success = useCallback(
    (description: ReactNode, options?: ToastShortcutOptions) =>
      toast({ ...options, description, variant: 'success' }),
    [toast]
  );

  const warning = useCallback(
    (description: ReactNode, options?: ToastShortcutOptions) =>
      toast({ ...options, description, variant: 'warning' }),
    [toast]
  );

  const error = useCallback(
    (description: ReactNode, options?: ToastShortcutOptions) =>
      toast({ ...options, description, variant: 'destructive' }),
    [toast]
  );

  const value = useMemo(
    () => ({
      toast,
      dismiss,
      success,
      warning,
      error,
      info,
    }),
    [dismiss, error, info, success, toast, warning]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-4 top-4 z-[120] flex flex-col gap-3 sm:left-auto sm:right-4 sm:w-full sm:max-w-sm">
        {toasts.map((item) => (
          <ToastCard key={item.id} toast={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider.');
  }

  return context;
}
